'use client';

import { useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useFloorplansByBedroom } from './hooks/useFloorplansByBedroom';
import { BedroomTabs } from './BedroomTabs';
import { MobileFloorPlanCarousel } from './MobileFloorPlanCarousel';
import { DesktopFloorPlanCarousel } from './DesktopFloorPlanCarousel';
import { TotalMonthlyPriceNotice } from '@/src/components/features/listings/TotalMonthlyPrice';
import type { Floorplan, Unit } from './UnitTable';
import * as styles from './unitsModule.styles';

// Interaction-only overlays: both render from click state, so their chunks
// stay out of the eager HDP bundle.
const UnitDetailModal = dynamic(() => import('./UnitDetailOverlay').then((mod) => mod.UnitDetailModal));
const SeeAllUnitsOverlay = dynamic(() => import('./SeeAllUnitsOverlay').then((mod) => mod.SeeAllUnitsOverlay));

type Props = {
  listing: PropertyDetailListing;
};

export const UnitsModule = ({ listing }: Props) => {
  const { bedroomOptions, floorplansByBedroom, hasFloorplans } = useFloorplansByBedroom(listing);

  const [activeBeds, setActiveBeds] = useState<number | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<{ unit: Unit; floorplan: Floorplan } | null>(null);
  const [seeAllFloorplan, setSeeAllFloorplan] = useState<Floorplan | null>(null);

  const effectiveBeds = activeBeds ?? bedroomOptions[0]?.beds ?? 0;

  const handleTabChange = useCallback((beds: number) => {
    setActiveBeds(beds);
  }, []);

  const handleUnitSelect = useCallback((unit: Unit, floorplan: Floorplan) => {
    setSelectedUnit({ unit, floorplan });
  }, []);

  const handleCloseUnitDetail = useCallback(() => {
    setSelectedUnit(null);
  }, []);

  const handleSeeAll = useCallback((floorplan: Floorplan) => {
    setSeeAllFloorplan(floorplan);
  }, []);

  const handleCloseSeeAll = useCallback(() => {
    setSeeAllFloorplan(null);
  }, []);

  const handleUnitSelectFromSeeAll = useCallback(
    (unit: Unit, floorplan: Floorplan) => {
      setSeeAllFloorplan(null);
      handleUnitSelect(unit, floorplan);
    },
    [handleUnitSelect],
  );

  if (!hasFloorplans) {
    return null;
  }

  return (
    <div className={styles.contentStack}>
      <BedroomTabs options={bedroomOptions} activeBeds={effectiveBeds} onTabChange={handleTabChange} />

      <TotalMonthlyPriceNotice listing={listing} className={styles.totalMonthlyPriceNotice} />

      {bedroomOptions.map((option) => {
        const floorplans = floorplansByBedroom.get(option.beds) ?? [];
        const isActive = option.beds === effectiveBeds;

        if (floorplans.length === 0) return null;

        return (
          <div
            key={option.beds}
            id={`bedroom-tabpanel-${option.beds}`}
            role="tabpanel"
            aria-labelledby={`bedroom-tab-${option.beds}`}
            hidden={!isActive}
          >
            {/* Mobile: Carousel */}
            <div className={styles.carouselContainer}>
              <MobileFloorPlanCarousel
                floorplans={floorplans}
                listing={listing}
                onUnitSelect={handleUnitSelect}
                onSeeAll={handleSeeAll}
              />
            </div>

            {/* Desktop: Paginated Carousel */}
            <div className={styles.desktopCarouselContainer}>
              <DesktopFloorPlanCarousel
                floorplans={floorplans}
                listing={listing}
                onUnitSelect={handleUnitSelect}
                onSeeAll={handleSeeAll}
              />
            </div>
          </div>
        );
      })}

      {selectedUnit && (
        <UnitDetailModal
          unit={selectedUnit.unit}
          floorplan={selectedUnit.floorplan}
          listing={listing}
          open={true}
          onClose={handleCloseUnitDetail}
        />
      )}

      {seeAllFloorplan && (
        <SeeAllUnitsOverlay
          floorplan={seeAllFloorplan}
          listing={listing}
          open={true}
          onClose={handleCloseSeeAll}
          onUnitSelect={handleUnitSelectFromSeeAll}
        />
      )}
    </div>
  );
};
